import React from 'react';
import { motion } from 'framer-motion';
import { Code, Database, Cloud, Smartphone, Shield, Zap, Network, FileText, Megaphone, Music, Settings, ArrowRight } from 'lucide-react';

const allServices = [
  // Existing 6
  {
    icon: Code,
    title: 'Web Development',
    description: 'Pages that load before people change their mind and tab away.',
    features: ['React & Next.js', 'TypeScript', 'Modern APIs', 'Progressive Web Apps'],
    impact: '+65% Speed',
    isNew: false,
  },
  {
    icon: Database,
    title: 'Smart Architecture',
    description: "We build things that don't fall over when the crowd shows up.",
    features: ['Cloud Databases', 'Redis Cache', 'Microservices', 'Auto-scaling'],
    impact: '+80% Scalability',
    isNew: false,
  },
  {
    icon: Cloud,
    title: 'Cloud & DevOps',
    description: 'Your site stays up during the sale, the launch, and the one time it goes viral.',
    features: ['AWS (primary)', 'Docker', 'CI/CD pipelines', 'Zero Downtime'],
    impact: '99.9% Uptime',
    isNew: false,
  },
  {
    icon: Smartphone,
    title: 'Mobile First',
    description: "Looks right whether someone's on a flagship phone or a five-year-old budget one.",
    features: ['Responsive Design', 'Touch Optimized', 'Fast Loading', 'Cross-browser'],
    impact: '+70% Mobile',
    isNew: false,
  },
  {
    icon: Shield,
    title: 'Fortress Security',
    description: "We follow Kenya's Data Protection Act so you don't end up explaining a leak to your customers.",
    features: ['SSL/HTTPS', 'DDoS Protection', 'Kenya Data Protection Act', 'Regular Audits'],
    impact: '+95% Security',
    isNew: false,
  },
  {
    icon: Zap,
    title: 'Automation & Integrations',
    description: 'Connect your site to M-Pesa, Mailchimp, or your CRM, and stop copy-pasting data by hand.',
    features: ['M-Pesa Integration', 'Email Marketing', 'Analytics Tracking', 'Workflow Automation'],
    impact: '+85% Automation',
    isNew: false,
  },
  // 4 new services
  {
    icon: Network,
    title: 'Networking & Infrastructure',
    description: 'Reliable internet, wired offices, and connected teams for businesses that can\'t afford downtime.',
    features: ['Network Design', 'LAN/WAN Setup', 'VPN & Firewall', 'Infrastructure Audits'],
    impact: '99.5% Uptime',
    isNew: true,
  },
  {
    icon: FileText,
    title: 'Digital Policy & Compliance',
    description: 'The policies and compliance frameworks that keep your company on the right side of the law.',
    features: ['Policy Drafting', 'DPA Compliance', 'IT Governance', 'Risk Assessment'],
    impact: '100% Compliant',
    isNew: true,
  },
  {
    icon: Megaphone,
    title: 'Digital Marketing',
    description: 'Website-focused growth: SEO, analytics, integrations, and campaigns that actually convert.',
    features: ['SEO Optimisation', 'Google Analytics', 'CRM Integrations', 'Conversion Funnels'],
    impact: '+60% Reach',
    isNew: true,
  },
  {
    icon: Settings,
    title: 'IT Support & Maintenance',
    description: 'Computers updated, printers running, software licensed, backups confirmed. All the IT work that keeps your team productive.',
    features: ['Hardware Upgrades', 'Software Updates', 'Printer Support', 'Backup Monitoring'],
    impact: '100% Uptime',
    isNew: true,
  },
  {
    icon: Music,
    title: 'AV System Consulting',
    description: 'Audio-visual setups designed and tuned for your specific space. We recommend, install, and configure for the best sound and picture.',
    features: ['System Recommendations', 'Acoustic Tuning', 'Room Calibration', 'Equipment Setup'],
    impact: 'Studio Quality',
    isNew: true,
  },
];

const ServiceCard = ({ service, index, onServiceClick }) => (
  <motion.div
    className={`relative group border bg-gray-900 overflow-hidden card-spotlight card-magnetic cursor-pointer ${
      service.isNew
        ? `border-l-4 border-l-orange-500 border-t border-r border-b border-gray-800 ${service.isNew ? 'forge-new-card' : ''}`
        : 'border-gray-800'
    }`}
    style={{ borderRadius: '2px' }}
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.06, duration: 0.4 }}
    whileHover={{ y: -4, boxShadow: service.isNew ? '0 0 28px rgba(249,115,22,0.2)' : '0 8px 32px rgba(0,0,0,0.4)' }}
    onClick={() => onServiceClick(service.title)}
  >
    <div className="p-7">
      {/* Header row */}
      <div className="flex items-start justify-between mb-5">
        <div
          className={`w-12 h-12 flex items-center justify-center ${
            service.isNew ? 'bg-orange-500' : 'bg-gray-800'
          }`}
          style={{ borderRadius: '2px' }}
        >
          <service.icon className={`w-6 h-6 ${service.isNew ? 'text-white' : 'text-orange-400'}`} />
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-600 uppercase tracking-wider mb-0.5">Impact</div>
          <div className={`text-sm font-black ${service.isNew ? 'text-orange-500' : 'text-white'}`}>
            {service.impact}
          </div>
        </div>
      </div>

      {/* New badge */}
      {service.isNew && (
        <div className="inline-block bg-orange-500 text-white text-xs font-black uppercase tracking-wider px-2 py-0.5 mb-3" style={{ borderRadius: '1px' }}>
          New
        </div>
      )}

      <h3 className="text-lg font-black text-white mb-3 group-hover:text-orange-400 transition-colors duration-200 uppercase tracking-wide">
        {service.title}
      </h3>

      <p className="text-sm text-gray-400 mb-5 leading-relaxed">
        {service.description}
      </p>

      <div className="space-y-2">
        {service.features.map((feature) => (
          <div key={feature} className="flex items-center gap-2 text-gray-400">
            <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${service.isNew ? 'bg-orange-500' : 'bg-gray-600'}`} />
            <span className="text-xs font-medium">{feature}</span>
          </div>
        ))}
      </div>
    </div>

    {/* Hover arrow */}
    <div className="absolute bottom-5 right-5 w-8 h-8 bg-orange-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200" style={{ borderRadius: '1px' }}>
      <ArrowRight className="w-4 h-4 text-white" />
    </div>
  </motion.div>
);

const ServicesSection = ({ onServiceClick }) => {
  return (
    <section id="services" className="py-28 relative bg-gray-950">
      <div className="container mx-auto px-6">

        {/* Section header */}
        <div className="mb-16">
          <div className="text-xs text-orange-500 font-black uppercase tracking-widest mb-4">
            What we do — 11 services
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="text-4xl md:text-6xl font-black leading-none tracking-tight">
              <span className="text-white">Every layer of your</span>
              <br />
              <span className="text-orange-500">digital presence.</span>
            </h2>
            <p className="text-gray-400 text-base max-w-sm md:text-right leading-relaxed">
              Web · Cloud · Network · Policy · Marketing · AV.
              Nothing dressed up as more than it is.
            </p>
          </div>
        </div>

        {/* Services grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-16 stagger-cards fade-in-up">
          {allServices.map((service, index) => (
            <ServiceCard key={service.title} service={service} index={index} onServiceClick={onServiceClick} />
          ))}
        </div>

        {/* Bottom CTA — hard-edge orange block */}
        <motion.div
          className="bg-orange-500 p-10 relative overflow-hidden"
          style={{ borderRadius: '2px' }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          {/* Decorative hard shapes */}
          <div className="absolute top-0 right-0 w-32 h-32 border-2 border-white/10" style={{ transform: 'translate(30%, -30%) rotate(45deg)' }} />
          <div className="absolute bottom-0 left-0 w-24 h-24 border-2 border-white/10" style={{ transform: 'translate(-30%, 30%)' }} />

          <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            <div>
              <h3 className="text-2xl md:text-3xl font-black text-white mb-2 uppercase tracking-tight">
                Got a Project? Let's Talk Specs, Not Buzzwords.
              </h3>
              <p className="text-orange-100/80 text-base max-w-xl">
                Tell us what you're building. We'll tell you honestly if it's a two-week job or a two-month one.
              </p>
            </div>
            <button
              className="shrink-0 bg-white hover:bg-gray-100 text-orange-600 px-8 py-4 font-black text-sm uppercase tracking-wider flex items-center gap-2 transition-colors duration-200 whitespace-nowrap"
              style={{ borderRadius: '2px' }}
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Let's Talk
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
